import { createContext, useContext, useState, ReactNode, useCallback, useRef, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useCreateShopifyCart,
  useGetShopifyCartInLanguage,
  getGetShopifyCartInLanguageQueryKey,
  useAddShopifyCartLine,
  useRemoveShopifyCartLineInLanguage,
  useUpdateShopifyCartCheckoutContact,
} from '@workspace/api-client-react';
import { toast } from '@/hooks/use-toast';
import { useLanguage } from '@/contexts/language-context';
import { trackAddToCart } from '@/lib/analytics';

interface CartContextType {
  cart: any;
  cartId: string | null;
  isLoading: boolean;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  addToCart: (merchandiseId: string) => Promise<void>;
  removeFromCart: (lineId: string) => void;
  saveCheckoutContact: (
    phone: string,
    fairUsageTermsAccepted: boolean,
  ) => Promise<void>;
  refreshCart: () => Promise<any | undefined>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const CART_ID_KEY = 'al_malaki_cart_id';

export function CartProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { t, shopifyLanguageCode } = useLanguage();
  const [cartId, setCartId] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(CART_ID_KEY);
    }
    return null;
  });
  const [isOpen, setIsOpen] = useState(false);
  const cartIdRef = useRef(cartId);
  cartIdRef.current = cartId;

  const setAndSaveCartId = (id: string) => {
    cartIdRef.current = id;
    setCartId(id);
    localStorage.setItem(CART_ID_KEY, id);
  };

  const {
    data: cart,
    isLoading: isCartLoading,
    isError: isCartError,
    error: cartError,
    refetch: refetchCart,
  } = useGetShopifyCartInLanguage(
    cartId as string,
    shopifyLanguageCode,
    {
      query: {
        enabled: !!cartId,
        queryKey: getGetShopifyCartInLanguageQueryKey(cartId as string, shopifyLanguageCode),
      },
    },
  );

  const createCart = useCreateShopifyCart();
  const addLine = useAddShopifyCartLine();
  const removeLine = useRemoveShopifyCartLineInLanguage();
  const updateCheckoutContact = useUpdateShopifyCartCheckoutContact();

  const clearSavedCart = useCallback(() => {
    const staleCartId = cartIdRef.current;
    cartIdRef.current = null;
    setCartId(null);
    setIsOpen(false);
    localStorage.removeItem(CART_ID_KEY);
    if (staleCartId) {
      const localizedCartKeyPrefix = `/api/shopify/carts/${staleCartId}/language/`;
      queryClient.removeQueries({
        predicate: ({ queryKey }) =>
          typeof queryKey[0] === 'string' && queryKey[0].startsWith(localizedCartKeyPrefix),
      });
    }
  }, [queryClient]);

  const isExpiredCartError = (error: unknown) => {
    const candidate = error as { status?: number; message?: string };
    return (
      candidate.status === 404 ||
      candidate.status === 410 ||
      /cart.*(does not exist|not found|expired|invalid)/i.test(candidate.message ?? '')
    );
  };

  useEffect(() => {
    if (
      cartId &&
      !isCartLoading &&
      isCartError &&
      isExpiredCartError(cartError)
    ) {
      clearSavedCart();
    }
  }, [cartId, isCartLoading, isCartError, cartError, clearSavedCart]);

  const invalidateCart = useCallback(() => {
    const currentCartId = cartIdRef.current;
    if (currentCartId) {
      const localizedCartKeyPrefix = `/api/shopify/carts/${currentCartId}/language/`;
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          typeof queryKey[0] === 'string' && queryKey[0].startsWith(localizedCartKeyPrefix),
      });
    }
  }, [queryClient]);

  const refreshCart = useCallback(async () => {
    if (!cartIdRef.current) return undefined;
    const result = await refetchCart();
    if (result.error) throw result.error;
    return result.data;
  }, [refetchCart]);

  const saveCheckoutContact = useCallback(
    async (
      phone: string,
      fairUsageTermsAccepted: boolean,
    ) => {
    const currentCartId = cartIdRef.current;
    if (!currentCartId) throw new Error('Cart is unavailable');
    await updateCheckoutContact.mutateAsync({
      cartId: currentCartId,
      data: {
        phone,
        whatsappTransactionalConsent: fairUsageTermsAccepted,
        fairUsageTermsAccepted,
      },
    });
    invalidateCart();
    },
    [invalidateCart, updateCheckoutContact],
  );

  const addToCart = useCallback(async (merchandiseId: string) => {
    try {
      if (cart?.lines?.some((line: any) => line.merchandise?.id === merchandiseId)) {
        setIsOpen(true);
        toast({ title: t('This unique piece is already in your bag') });
        return;
      }

      const createNewCart = async () => {
        const newCart = await createCart.mutateAsync({
          data: { merchandiseId, quantity: 1, languageCode: shopifyLanguageCode },
        });
        trackAddToCart({ id: merchandiseId, quantity: 1 });
        setAndSaveCartId(newCart.id);
        setIsOpen(true);
      };

      const existingCartId = cartIdRef.current;
      if (!existingCartId) {
        await createNewCart();
        return;
      }

      try {
        await addLine.mutateAsync({
          cartId: existingCartId,
          data: { merchandiseId, quantity: 1, languageCode: shopifyLanguageCode }
        });
        trackAddToCart({ id: merchandiseId, quantity: 1 });
        invalidateCart();
        setIsOpen(true);
      } catch (error) {
        if (!isExpiredCartError(error)) throw error;
        clearSavedCart();
        await createNewCart();
      }
    } catch {
      toast({ title: t('Failed to add item'), variant: 'destructive' });
    }
  }, [cart, createCart, addLine, invalidateCart, clearSavedCart, shopifyLanguageCode, t]);

  const removeFromCart = useCallback((lineId: string) => {
    if (!cartIdRef.current) return;
    removeLine.mutate({ cartId: cartIdRef.current, lineId, languageCode: shopifyLanguageCode }, {
      onSuccess: invalidateCart,
      onError: () => toast({ title: t('Failed to remove item'), variant: 'destructive' })
    });
  }, [removeLine, invalidateCart, shopifyLanguageCode, t]);

  return (
    <CartContext.Provider
      value={{
        cart,
        cartId,
        isLoading: isCartLoading,
        isOpen,
        setIsOpen,
        addToCart,
        removeFromCart,
        saveCheckoutContact,
        refreshCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within CartProvider');
  return context;
}